import React from 'react';
import Icon from '../../../components/AppIcon';

const InvoiceStats = ({ invoices, loading }) => {
  const getEffectiveStatus = (inv) => inv?.invoice_status || inv?.status?.name || 'draft';

  const calculateStats = () => {
    if (!invoices || invoices?.length === 0) {
      return { totalRevenue: 0, pendingAmount: 0, overdueAmount: 0, paidCount: 0, draftCount: 0, sentCount: 0, collectionRate: 0 };
    }

    const totalRevenue = invoices?.reduce((sum, inv) => sum + parseFloat(inv?.total_amount || 0), 0);
    const pendingAmount = invoices
      ?.filter(inv => ['pending', 'partial', 'sent']?.includes(getEffectiveStatus(inv)?.toLowerCase()))
      ?.reduce((sum, inv) => sum + parseFloat(inv?.amount_due || 0), 0);
    const overdueAmount = invoices
      ?.filter(inv => getEffectiveStatus(inv)?.toLowerCase() === 'overdue' ||
        (inv?.due_date && new Date(inv?.due_date) < new Date() && !['paid', 'cancelled']?.includes(getEffectiveStatus(inv)?.toLowerCase())))
      ?.reduce((sum, inv) => sum + parseFloat(inv?.amount_due || 0), 0);
    const paidCount = invoices?.filter(inv => getEffectiveStatus(inv)?.toLowerCase() === 'paid')?.length;
    const draftCount = invoices?.filter(inv => getEffectiveStatus(inv)?.toLowerCase() === 'draft')?.length;
    const sentCount = invoices?.filter(inv => inv?.sent_at)?.length;
    const totalPaid = invoices?.reduce((sum, inv) => sum + parseFloat(inv?.amount_paid || 0), 0);
    const collectionRate = totalRevenue > 0 ? Math.round((totalPaid / totalRevenue) * 100) : 0;

    return { totalRevenue, pendingAmount, overdueAmount, paidCount, draftCount, sentCount, collectionRate };
  };

  const stats = calculateStats();

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0)?.toFixed(2)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  };

  const statCards = [
    {
      label: 'Total Revenue',
      value: formatCurrency(stats?.totalRevenue),
      icon: 'DollarSign',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      sub: `${stats?.sentCount} sent`,
    },
    {
      label: 'Pending Payments',
      value: formatCurrency(stats?.pendingAmount),
      icon: 'Clock',
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      sub: `${stats?.draftCount} drafts`,
    },
    {
      label: 'Overdue Amount',
      value: formatCurrency(stats?.overdueAmount),
      icon: 'AlertTriangle',
      color: 'text-error',
      bgColor: 'bg-error/10',
      sub: overdueAmount => overdueAmount > 0 ? 'Needs attention' : 'All current',
    },
    {
      label: 'Collection Rate',
      value: `${stats?.collectionRate}%`,
      icon: 'TrendingUp',
      color: 'text-success',
      bgColor: 'bg-success/10',
      sub: `${stats?.paidCount} paid invoices`,
    },
  ];

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[1, 2, 3, 4]?.map((i) => (
          <div key={i} className="bg-card rounded-lg border border-border p-4 animate-pulse">
            <div className="h-4 bg-muted rounded w-1/2 mb-2"></div>
            <div className="h-8 bg-muted rounded w-3/4"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {statCards?.map((stat, index) => (
        <div
          key={index}
          className="bg-card rounded-lg border border-border shadow-elevation-1 p-4 hover:shadow-elevation-2 transition-smooth"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-caption text-muted-foreground">{stat?.label}</span>
            <div className={`w-10 h-10 rounded-lg ${stat?.bgColor} flex items-center justify-center`}>
              <Icon name={stat?.icon} size={20} className={stat?.color} />
            </div>
          </div>
          <p className="text-2xl font-heading font-bold text-foreground">{stat?.value}</p>
          {stat?.sub && (
            <p className="text-xs text-muted-foreground mt-1">
              {typeof stat?.sub === 'function' ? stat?.sub(stats?.overdueAmount) : stat?.sub}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

export default InvoiceStats;